/**
 * Loading Overlay Component
 * Full-screen overlay for blocking operations (use sparingly!)
 * Only for critical operations like payment, final submission
 */

import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import InlineSpinner from './InlineSpinner';
import { UploadProgress } from './ProgressBar';
import { loadingConfig } from './loadingConfig';
import { spacing, colors } from '../../theme/tokens';

const OverlayContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: loadingConfig.colors.overlay.background,
  backdropFilter: 'blur(4px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
});

const LoadingContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.lg,
  padding: spacing.xl,
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  maxWidth: '400px',
  width: '90%',
});

const LoadingText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: colors.text.primary,
  textAlign: 'center',
});

/**
 * Loading Overlay
 * IMPORTANT: Only use for critical blocking operations!
 *
 * @param {Object} props
 * @param {string} props.message - Loading message (default: "טוען...")
 * @param {number} props.progress - Progress value 0-100 (shows progress bar if provided)
 * @param {boolean} props.show - Whether to show overlay
 * @param {React.ReactNode} props.children - Custom content (overrides default)
 */
export default function LoadingOverlay({
  message = 'טוען...',
  progress,
  show = true,
  children,
}) {
  if (!show) return null;

  return (
    <OverlayContainer role="status" aria-live="polite" aria-label={message}>
      <LoadingContent>
        {children || (
          <>
            <InlineSpinner size="lg" />
            <LoadingText>{message}</LoadingText>
            {typeof progress === 'number' && <UploadProgress value={progress} />}
          </>
        )}
      </LoadingContent>
    </OverlayContainer>
  );
}
